// =============================================================================
//  Copyright : 2019 semantics GmbH
//  Date      : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 156393 $
//  Purpose   : transcription parts
// =============================================================================
// ---Third-party imports
import React, {useEffect, useState} from 'react'
import {Button, Transition, Image} from 'semantic-ui-react'
import clsx from 'clsx'

// ---Framework imports
import {mapItems, items, dict, flatten} from 'ldash'
import {Actions, Sel} from 'uix/registries'
import log from 'uix/logging'
import {WikiPane} from 'uix/wiki/render'
import {useWikiRendered} from 'uix/wiki/hooks'
import {useRouter, useSelector} from 'uix/hooks'
import {Link, XMenu} from 'uix/components'

// ---Local imports
import S from './components.scss'

// ---Globals
export const TranscriptionWiki = ({ident}) => {
  let wikidata = useWikiRendered({ident})
  return <WikiPane rendered={wikidata?.rendered} />
}

const NAV = {
  account: 'tabUserAccount',
  transcriptions: 'transcriptions',
  users: 'tabUserManage',
  transcriptionHowtos: 'transcriptionHowtos',
}

const NAVIcons = {
  account: 'user circle outline',
  transcriptions: 'transcript',
  users: 'users',
  transcriptionHowtos: 'help',
}

const SUBNAV = {
  transcriptions: {
    activity: 'lastModifications',
    review: 'transcriptionReview',
    begun: 'transcriptionsBegun',
    published: 'transcriptionsPublished',
  },
  transcriptionHowtos: {
    specs: 'transcriptionSpecs',
    guidelines: 'transcriptionGuidelines',
    trguidelines: 'transcriptionTrGuidelines',
    terms: 'termsOfUse',
  },
}

const DebugCompare = ({}) => {
  let {request} = useRouter()
  let apiurl = useSelector(Sel.apiurl)
  return (
    <a
      style={{float: 'right'}}
      href={apiurl.href(`/transcript/${request.tabname}/${request.id || ''}`)}
      target="_compare"
    >
      [compare]
    </a>
  )
}

const NavRail = ({onSubMenuChange, logoUrl}) => {
  let {request} = useRouter()
  let i18n = useSelector(Sel.i18n)

  // determine chosen subnav group
  let tabname2subnavname = dict(
    flatten(mapItems(SUBNAV, (subnavname, v) => mapItems(v, tabname => [tabname, subnavname])))
  )

  let subnavname = tabname2subnavname[request.tabname]

  useEffect(() => {
    subnavname ? onSubMenuChange(true) : onSubMenuChange(false)
  }, [subnavname])
  // build main nav
  let navitems = mapItems(NAV, (navname, caption) => {
    let tabname = navname
    let is_active = tabname == request.tabname
    if (SUBNAV[navname]) {
      tabname = items(SUBNAV[navname])[0][0]
      is_active = navname == subnavname
    }
    return (
      <XMenu.LinkItem
        icon={NAVIcons[navname]}
        content={i18n.get(caption)}
        key={tabname}
        tabname={tabname}
        active={is_active}
      />
    )
  })

  // Erstelle die Subnav-Items, falls vorhanden
  let subnavItems = null

  if (subnavname) {
    subnavItems = mapItems(SUBNAV[subnavname], (tabname, caption) => {
      let isActive = tabname === request.tabname
      return (
        <XMenu.LinkItem
          content={i18n.get(caption)}
          key={tabname}
          tabname={tabname}
          active={isActive}
        ></XMenu.LinkItem>
      )
    })
  }

  return (
    <div className={S.menuContainer}>
      <XMenu className={S.navRail} vertical borderless padded attached size="large">
        <div className={S.top}>
          {logoUrl && (
            <XMenu.LinkItem key="logo">
              <Image src={logoUrl} style={{height: '2.75rem'}} />
            </XMenu.LinkItem>
          )}
        </div>
        <div className={S.center}>
          {navitems}
          <XMenu.LinkItem
            icon="discussions"
            content={i18n.get('discussionBoard')}
            key={'discussionBoard'}
            // tabname={tabname}
            path={'https://e-manuscripta-forum.ch/'}
          />
        </div>
        <div className={S.bottom}>
          <XMenu.Item>
            <DebugCompare />
          </XMenu.Item>
        </div>
      </XMenu>
      {subnavname && (
        <XMenu className={S.navDrawer} secondary vertical attached>
          {subnavItems}
        </XMenu>
      )}
    </div>
  )
}

export const TranscriptionLayout = ({children}) => {
  let apiurl = useSelector(Sel.apiurl)
  let url = apiurl.href(`/favicon.ico`)
  //!TODO: make it depending on children rendertime
  let [show, setShow] = useState(false)
  let [hasSubMenu, setHasSubMenu] = useState(false)
  useEffect(() => {
    setTimeout(() => setShow(true), 550)
  }, [children])

  const handleSubMenuChange = hasSubMenu => {
    setHasSubMenu(hasSubMenu)
  }

  return (
    <div className={S.layout}>
      <NavRail logoUrl={url} onSubMenuChange={handleSubMenuChange} />
      <Transition visible={show} animation="fade" duration={300}>
        <div className={clsx(S.content, hasSubMenu && S.hasDrawer)}>{children}</div>
      </Transition>
    </div>
  )
}

export const BasicButton = ({link, ...props}) => {
  let button = <Button {...props} basic compact size="tiny" />
  if (link) {
    button = <Link {...link}>{button}</Link>
  }
  return button
}

export const UserInfo = ({fullname, email, puid, showActivityButton = false}) => (
  <>
    <span style={{minWidth: '10em', display: 'inline-block'}}>{fullname || '[n/a]'} </span>
    <tt style={{minWidth: '10em', display: 'inline-block', backgroundColor: '#CCC'}}>
      {email || '[n/a]'}
    </tt>
    {showActivityButton && (
      <BasicButton floated="right" icon="cog" title="Aktivität" style={{marginRight: '20px'}} />
    )}
  </>
)
